import React, { useState, useEffect, useRef } from 'react';
import { ChevronLeft, ChevronRight, Quote, Star } from 'lucide-react';
import { motion, useAnimation, useInView } from 'framer-motion';
import '../styles/SimpleAnimatedTestimonials.scss';

const AVATAR_COLORS = ['#8b6f47', '#5e8c78', '#6482a0', '#b47858', '#7896a0'];

const testimonialData = [
  { id: 1, name: "Anna Z.", type: "Home Seller", content: "Lily sold my home under the most challenging of circumstances in a market unfavorable towards sellers. She skillfully navigated every obstacle and secured the best price possible. Lily is an agent who will go above and beyond for her clients." },
  { id: 2, name: "Christine L.", type: "Home Buyer", content: "Lily helped me achieve what I thought was an impossible pipe dream — owning a condo in my dream neighborhood within my budget in one of the most competitive markets in the country, all while displaying incredible patience and encouragement." },
  { id: 3, name: "Sahar B.", type: "Home Seller", content: "Lily was professional, detail-oriented, and communicative. She spent a lot of time on marketing, took great care re-staging the property, and consulted me on the right selling strategy. Once I hired her, a house that was on the market for 6 weeks sold within 2 weeks." },
  { id: 4, name: "Lillian K.", type: "Home Buyer", content: "I cannot imagine how we could have landed our first home, in a competitive market like San Francisco, without our amazing partnership with Lily and Bryant. They made every single granular step concrete and clear for us." },
  { id: 5, name: "Brooke M.", type: "Buyer & Seller", content: "She went above and beyond to ensure I found the perfect condo, then later helped me sell above listing price. Her exceptional communication skills stood out from day one — always prompt and keeping me informed every step of the way." },
  { id: 6, name: "Timothy L.", type: "Home Buyer", content: "They were fully honest about how tight the timeline was — but said it was definitely doable, and got to work right away. The first property they found me was a match made in heaven. We locked down the property by mid-April." },
  { id: 7, name: "Phillip K.", type: "Home Buyer", content: "Almost every other realtor we encountered throughout the months of our search knew of Lily and Bryant and commented on how fortunate we were to be working with them. I could not recommend them enough." },
  { id: 8, name: "Steve B.", type: "Home Buyer", content: "From our very first meeting, it was evident how enthusiastic they were about helping us find our dream home. Their support, patience, and clear communication made the entire process smooth and stress-free." },
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1, delayChildren: 0.2 } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } },
};

export default function SimpleAnimatedTestimonials({ autoRotateInterval = 6000 }) {
  const [activeIndex, setActiveIndex] = useState(0);
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, amount: 0.2 });
  const controls = useAnimation();

  useEffect(() => {
    if (isInView) controls.start('visible');
  }, [isInView, controls]);

  useEffect(() => {
    if (autoRotateInterval <= 0 || testimonialData.length <= 1) return;
    const interval = setInterval(() => {
      setActiveIndex(prev => (prev + 1) % testimonialData.length);
    }, autoRotateInterval);
    return () => clearInterval(interval);
  }, [autoRotateInterval]);

  const handlePrev = () => setActiveIndex(prev => (prev - 1 + testimonialData.length) % testimonialData.length);
  const handleNext = () => setActiveIndex(prev => (prev + 1) % testimonialData.length);

  return (
    <div ref={sectionRef} className="sat-wrap">
      <motion.div
        initial="hidden"
        animate={controls}
        variants={containerVariants}
        className="sat-grid"
      >
        {/* Card area */}
        <motion.div variants={itemVariants} className="sat-card-area">
          <div className="sat-quote-icon">
            <Quote size={48} strokeWidth={1} />
          </div>
          <div className="sat-cards">
            {testimonialData.map((t, index) => {
              const avatarColor = AVATAR_COLORS[t.name.charCodeAt(0) % AVATAR_COLORS.length];
              return (
                <div
                  key={t.id}
                  className={`sat-card${index === activeIndex ? ' sat-card--active' : ''}`}
                >
                  <div className="sat-card-header">
                    <div className="sat-author">
                      <div className="sat-avatar" style={{ background: avatarColor }}>
                        {t.name.charAt(0)}
                      </div>
                      <div>
                        <div className="sat-name">{t.name}</div>
                        <div className="sat-role">{t.type}</div>
                      </div>
                    </div>
                    <div className="sat-stars">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} size={13} fill="#c8a96e" color="#c8a96e" />
                      ))}
                    </div>
                  </div>
                  <div className="sat-separator" />
                  <p className="sat-content">"{t.content}"</p>
                </div>
              );
            })}
          </div>
        </motion.div>

        {/* Navigation */}
        <motion.div variants={itemVariants} className="sat-nav">
          <button onClick={handlePrev} className="sat-btn" aria-label="Previous testimonial">
            <ChevronLeft size={16} />
          </button>
          <div className="sat-dots">
            {testimonialData.map((_, index) => (
              <button
                key={index}
                className={`sat-dot${index === activeIndex ? ' sat-dot--active' : ''}`}
                onClick={() => setActiveIndex(index)}
                aria-label={`Go to testimonial ${index + 1}`}
              />
            ))}
          </div>
          <button onClick={handleNext} className="sat-btn" aria-label="Next testimonial">
            <ChevronRight size={16} />
          </button>
        </motion.div>
      </motion.div>
    </div>
  );
}
